import { moveSelection, getPointBefore, getEditorString, getRange, isCollapsed, createPluginFactory, getPlugin, withoutNormalizing, withoutMergingHistory, deleteText, insertText } from '@udecode/plate-core';
import { Range } from 'slate';
import data from '@emoji-mart/data';
import { comboboxActions } from '@udecode/plate-combobox';

const ELEMENT_EMOJI = 'emoji';
const ELEMENT_EMOJI_INPUT = 'emoji_input';
const EMOJI_TRIGGER = ':';
const EMOJI_MAX_RESULT = 90;

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var lib = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});

/**
 * Constants.
 */

// We make this a function so it can be tested in describe block mocks with Jest.
var IS_MAC = () => typeof window != 'undefined' && /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);

var MODIFIERS = {
  alt: 'altKey',
  control: 'ctrlKey',
  meta: 'metaKey',
  shift: 'shiftKey'
};

// We make this a function so it can be tested in describe block mocks with Jest.
var ALIASES = () => ({
  add: '+',
  break: 'pause',
  cmd: 'meta',
  command: 'meta',
  ctl: 'control',
  ctrl: 'control',
  del: 'delete',
  down: 'arrowdown',
  esc: 'escape',
  ins: 'insert',
  left: 'arrowleft',
  mod: IS_MAC() ? 'meta' : 'control',
  opt: 'alt',
  option: 'alt',
  return: 'enter',
  right: 'arrowright',
  space: ' ',
  spacebar: ' ',
  up: 'arrowup',
  win: 'meta',
  windows: 'meta'
});

var CODES = {
  backspace: 8,
  tab: 9,
  enter: 13,
  shift: 16,
  control: 17,
  alt: 18,
  pause: 19,
  capslock: 20,
  escape: 27,
  ' ': 32,
  pageup: 33,
  pagedown: 34,
  end: 35,
  home: 36,
  arrowleft: 37,
  arrowup: 38,
  arrowright: 39,
  arrowdown: 40,
  insert: 45,
  delete: 46,
  meta: 91,
  numlock: 144,
  scrolllock: 145,
  ';': 186,
  '=': 187,
  ',': 188,
  '-': 189,
  '.': 190,
  '/': 191,
  '`': 192,
  '[': 219,
  '\\': 220,
  ']': 221,
  '\'': 222
};

for (var f = 1; f < 20; f++) {
  CODES['f' + f] = 111 + f;
}

/**
 * Is hotkey?
 */

function isHotkey(hotkey, options, event) {
  if (options && !('byKey' in options)) {
    event = options;
    options = null;
  }

  if (!Array.isArray(hotkey)) {
    hotkey = [hotkey];
  }

  var array = hotkey.map(function (string) {
    return parseHotkey(string, options);
  });
  var check = function check(e) {
    return array.some(function (object) {
      return compareHotkey(object, e);
    });
  };
  var ret = event == null ? check : check(event);
  return ret;
}

function isCodeHotkey(hotkey, event) {
  return isHotkey(hotkey, event);
}

function isKeyHotkey(hotkey, event) {
  return isHotkey(hotkey, { byKey: true }, event);
}

/**
 * Parse.
 */

function parseHotkey(hotkey, options) {
  var byKey = options && options.byKey;
  var ret = {};

  // Special case to handle the `+` key since we use it as a separator.
  hotkey = hotkey.replace('++', '+add');
  var values = hotkey.split('+');
  var length = values.length;

  // Ensure that all the modifiers are set to false unless the hotkey has them.

  for (var k in MODIFIERS) {
    ret[MODIFIERS[k]] = false;
  }

  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = values[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var value = _step.value;

      var optional = value.endsWith('?') && value.length > 1;

      if (optional) {
        value = value.slice(0, -1);
      }

      var name = toKeyName(value);
      var modifier = MODIFIERS[name];

      if (length === 1 || !modifier) {
        if (byKey) {
          ret.key = name;
        } else {
          ret.which = toKeyCode(value);
        }
      }

      if (modifier) {
        ret[modifier] = optional ? null : true;
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return ret;
}

/**
 * Compare.
 */

function compareHotkey(object, event) {
  for (var key in object) {
    var expected = object[key];
    var actual = void 0;

    if (expected == null) {
      continue;
    }

    if (key === 'key' && event.key != null) {
      actual = event.key.toLowerCase();
    } else if (key === 'which') {
      actual = expected === 91 && event.which === 93 ? 91 : event.which;
    } else {
      actual = event[key];
    }

    if (actual == null && expected === false) {
      continue;
    }

    if (actual !== expected) {
      return false;
    }
  }

  return true;
}

/**
 * Utils.
 */

function toKeyCode(name) {
  name = toKeyName(name);
  var code = CODES[name] || name.toUpperCase().charCodeAt(0);
  return code;
}

function toKeyName(name) {
  name = name.toLowerCase();
  name = ALIASES()[name] || name;
  return name;
}

/**
 * Export.
 */

exports.default = isHotkey;
exports.isHotkey = isHotkey;
exports.isCodeHotkey = isCodeHotkey;
exports.isKeyHotkey = isKeyHotkey;
exports.parseHotkey = parseHotkey;
exports.compareHotkey = compareHotkey;
exports.toKeyCode = toKeyCode;
exports.toKeyName = toKeyName;
});

var isHotkey = unwrapExports(lib);
lib.isHotkey;
lib.isCodeHotkey;
lib.isKeyHotkey;
lib.parseHotkey;
lib.compareHotkey;
lib.toKeyCode;
lib.toKeyName;

/**
 * TODO: move to core
 */

// TODO: move to core
const moveSelectionByOffset = (editor, {
  query = () => true
} = {}) => event => {
  const {
    selection
  } = editor;

  if (!selection || Range.isExpanded(selection) || !query(editor)) {
    return false;
  }

  if (isHotkey('left', event)) {
    event.preventDefault();
    moveSelection(editor, {
      unit: 'offset',
      reverse: true
    });
    return true;
  }

  if (isHotkey('right', event)) {
    event.preventDefault();
    moveSelection(editor, {
      unit: 'offset'
    });
    return true;
  }
}; // ------>


const getOnKeyDownEmoji = options => editor => event => {
  return moveSelectionByOffset(editor, options)(event);
};

const defaultEmojiTriggeringControllerOptions = {
  limitTriggeringChars: 3,
  maxTextToSearch: 30
};
class EmojiTriggeringController {
  text = '';
  isTriggering = false;
  hasTriggeringMark = false;

  constructor(trigger = ':', options = defaultEmojiTriggeringControllerOptions) {
    this.trigger = trigger;
    this.options = options;
  }

  setText(text) {
    this.text = text;
    this.hasTriggeringMark = this.isWithTriggeringMark(text);
    this.isTriggering = this.hasTriggeringMark && this.text.length >= this.options.limitTriggeringChars;
  }

  isWithTriggeringMark(text) {
    return new RegExp(`^${this.trigger}.*`).test(text);
  }

  getText() {
    return this.hasTriggeringMark ? this.text.slice(1) : this.text;
  }

  getOptions() {
    return this.options;
  }

  getTextSize() {
    return this.text.length;
  }

  reset() {
    this.text = '';
    this.isTriggering = false;
    this.hasTriggeringMark = false;
  }

}

class PrepareData {
  _hash = {};
  _keys = [];

  constructor(library) {
    this._emojis = library.emojis;
    this.init();
  }

  init() {
    Object.values(this._emojis).forEach(emoji => {
      const searchableString = this.createSearchableString(emoji);

      this._keys.push(searchableString);

      this._hash[searchableString] = emoji.id;
    });
  }

  createSearchableString(emoji) {
    const {
      id,
      name,
      keywords
    } = emoji;
    return `${id},${this.getName(name)},${keywords.join(',')}`;
  }

  getName(name) {
    return name.toLowerCase().split(' ').join(',');
  }

  get keys() {
    return this._keys;
  }

  getEmoji(key) {
    return this._emojis[key];
  }

  getEmojiId(key) {
    return this._hash[key];
  }

}

class AIndexSearch {
  result = [];
  scores = {};
  maxResult = EMOJI_MAX_RESULT;

  constructor() {
    this.data = new PrepareData(data);
  }

  search(input) {
    const value = input.toLowerCase();
    this.createSearchResult(value);
    this.sortResultByScores(this.result, this.scores);
    return this;
  }

  createSearchResult(value) {
    this.scores = {};
    this.result = [];

    for (const key of this.data.keys) {
      const score = key.indexOf(`${value}`);
      if (score === -1) continue;
      const emojiId = this.data.getEmojiId(key);
      this.result.push(emojiId);
      this.scores[emojiId] || (this.scores[emojiId] = 0);
      this.scores[emojiId] += emojiId === value ? 0 : score + 1;
    }
  }

  sortResultByScores(result, scores) {
    result.sort((a, b) => {
      const aScore = scores[a];
      const bScore = scores[b];

      if (aScore === bScore) {
        return a.localeCompare(b);
      }

      return aScore - bScore;
    });
  }

  get() {
    const emojis = [];

    for (const key of this.result) {
      var _this$data;

      const emoji = (_this$data = this.data) === null || _this$data === void 0 ? void 0 : _this$data.getEmoji(key);
      emojis.push(this.transform(emoji));
      if (emojis.length >= this.maxResult) break;
    }

    return emojis;
  }

}

class EmojiIndexSearch extends AIndexSearch {
  constructor() {
    super();
  }

  static getInstance() {
    if (!EmojiIndexSearch.instance) {
      EmojiIndexSearch.instance = new EmojiIndexSearch();
    }

    return EmojiIndexSearch.instance;
  }

  transform(emoji) {
    const {
      id,
      name,
      skins
    } = emoji;
    return {
      key: id,
      text: name,
      data: {
        id,
        emoji: skins[0].native,
        name,
        text: name
      }
    };
  }

}

const getNextPoint = (editor, endPoint) => getPointBefore(editor, endPoint, {
  unit: 'character',
  distance: 1
});

const getFoundText = (editor, start, end) => getEditorString(editor, getRange(editor, start, end));

const isBreakingCharInText = text => /^\s/.test(text);

const getFindTriggeringInput = (editor, emojiTriggeringController) => (text = '') => {
  const selection = editor.selection;
  const startPoint = editor.selection;
  let currentText = text;
  let endPoint = selection.anchor;
  let nextPoint;
  let repeat = emojiTriggeringController.getOptions().maxTextToSearch;

  do {
    emojiTriggeringController.setText(currentText);
    if (emojiTriggeringController.hasTriggeringMark) break;
    nextPoint = getNextPoint(editor, endPoint);
    const foundText = getFoundText(editor, startPoint, nextPoint);
    endPoint = nextPoint;
    currentText = `${foundText}${text}`;

    if (isBreakingCharInText(foundText)) {
      emojiTriggeringController.reset();
      break;
    }
  } while (--repeat > 0);
};

const withEmoji = (editor, {
  options: {
    id,
    emojiTriggeringController
  }
}) => {
  const emojiIndexSearch = EmojiIndexSearch.getInstance();
  const findTheTriggeringInput = getFindTriggeringInput(editor, emojiTriggeringController);
  const {
    apply,
    insertText
  } = editor;

  editor.insertText = text => {
    const {
      selection
    } = editor;
    if (!selection || !isCollapsed(selection)) return insertText(text);
    findTheTriggeringInput(text);
    return insertText(text);
  };

  editor.apply = operation => {
    apply(operation);

    switch (operation.type) {
      case 'set_selection':
        emojiTriggeringController.reset();
        comboboxActions.reset();
        break;

      case 'insert_text':
        if (emojiTriggeringController.isTriggering) {
          const searchText = emojiTriggeringController.getText();
          comboboxActions.items(emojiIndexSearch.search(searchText).get());
          comboboxActions.open({
            activeId: id,
            text: '',
            targetRange: editor.selection
          });
        }

        break;

      case 'remove_text':
        findTheTriggeringInput();

        if (emojiTriggeringController.isTriggering) {
          const searchText = emojiTriggeringController.getText();
          comboboxActions.items(emojiIndexSearch.search(searchText).get());
          comboboxActions.open({
            activeId: id,
            text: '',
            targetRange: editor.selection
          });
          break;
        }

        emojiTriggeringController.reset();
        comboboxActions.reset();
        break;
    }
  };

  return editor;
};

const createEmojiPlugin = createPluginFactory({
  key: ELEMENT_EMOJI,
  isElement: true,
  isInline: true,
  isVoid: true,
  handlers: {
    onKeyDown: getOnKeyDownEmoji()
  },
  withOverrides: withEmoji,
  options: {
    trigger: EMOJI_TRIGGER,
    createEmoji: item => item.data.emoji,
    emojiTriggeringController: new EmojiTriggeringController(EMOJI_TRIGGER)
  },
  plugins: [{
    key: ELEMENT_EMOJI_INPUT,
    isElement: true,
    isInline: true
  }],
  then: (_, {
    key,
    options: {
      trigger,
      createEmoji,
      emojiTriggeringController
    }
  }) => ({
    options: {
      id: key,
      trigger,
      createEmoji,
      emojiTriggeringController
    }
  })
});

const getEmojiOnSelectItem = ({
  key = ELEMENT_EMOJI
} = {}) => (editor, item) => {
  const {
    options: {
      createEmoji,
      emojiTriggeringController
    }
  } = getPlugin(editor, key);
  withoutNormalizing(editor, () => {
    withoutMergingHistory(editor, () => deleteText(editor, {
      distance: emojiTriggeringController.getTextSize(),
      reverse: true
    }));
    emojiTriggeringController.reset();
    const value = createEmoji(item);
    insertText(editor, value);
  });
  return comboboxActions.reset();
};

export { ELEMENT_EMOJI, ELEMENT_EMOJI_INPUT, EMOJI_MAX_RESULT, EMOJI_TRIGGER, EmojiIndexSearch, EmojiTriggeringController, createEmojiPlugin, defaultEmojiTriggeringControllerOptions, getEmojiOnSelectItem, getFindTriggeringInput, getOnKeyDownEmoji, withEmoji };
//# sourceMappingURL=index.es.js.map
